import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ContentType, QueryRequest, Response, SignatureImage, SignatureLastPosition, SignatureProfile, SignCheckPinRequest, SignDocumentRequest, SigntrustCertificate, UserSignProfileRequest, UserSignProfileResponse } from '@models/index.model';
import { UtilsService } from '@services/index.service';
import { BehaviorSubject, finalize, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SigntrustService {
  private readonly file = new BehaviorSubject<string | null>(null);
  private readonly UrlAPI = environment.apiUrl;

  constructor(private readonly http: HttpClient, private readonly _utils: UtilsService) {}

  public setFile(file: string): void {
    this.file.next(file);
  }

  public getFile(): Observable<string | null> {
    return this.file.asObservable().pipe(finalize(() => this.file.next(null)));
  }

  public onSignDocument(signDocumentRequest: SignDocumentRequest, file: File): Observable<any> {
    const query = this.UrlAPI + QueryRequest.sign;
    const httpHeaders = new HttpHeaders({
      timeout: environment.timeoutSign.toString(),
    });
    const formdata: FormData = new FormData();
    formdata.append('', file, file.name);
    formdata.append('jsonUploadData', this._utils.stringifyData(signDocumentRequest));

    return this.http.post(query, formdata, {
    observe: 'response',
    responseType: 'arraybuffer',
    headers: httpHeaders,
    });
  }

  public getSigntrustCertificates(): Observable<Response<Array<SigntrustCertificate>>> {
    const query = this.UrlAPI + QueryRequest.signCertificateList;
    return this.http.get<Response<Array<SigntrustCertificate>>>(query);
  }

  public checkPin(
    checkPinRequest: SignCheckPinRequest
  ): Observable<Response<any>> {
    const query = this.UrlAPI + QueryRequest.signCheckPin;
    return this.http.post<Response<any>>(query, checkPinRequest);
  }

  public getRemoteDocument(request: string): Observable<Response<String>> {
    const query = this.UrlAPI + QueryRequest.signRemoteDocument + '?request=' + request;
    return this.http.get<Response<String>>(query);
  }
  public deleteRemoteDocument(request: string): Observable<Response<String>> {
    const query = this.UrlAPI + QueryRequest.signRemoteDocument + '?request=' + request;
    return this.http.delete<Response<String>>(query);
  }

  public getSignProfile(signProfileRequest: UserSignProfileRequest): Observable<Response<UserSignProfileResponse>> {
    const query = this.UrlAPI + QueryRequest.userSignProfile + `?source=${signProfileRequest.source}&key=${signProfileRequest.key}`;
    return this.http.get<Response<UserSignProfileResponse>>(query);
  }

  public setSignProfile(file: File, signProfileRequest: UserSignProfileRequest): Observable<Response<UserSignProfileResponse>> {
    const query = this.UrlAPI + QueryRequest.userSignProfile;
    const httpHeaders = new HttpHeaders({'Content-Type' : ContentType.formDataType});
    const options = { headers: httpHeaders };
    const formdata: FormData = new FormData();
    if (file) {
      formdata.append('', file, file.name);
    }
    formdata.append('jsonData', this._utils.stringifyData(signProfileRequest));
    return this.http.put<Response<UserSignProfileResponse>>(query, formdata, options);
  }

  // NEW SIGNTRUST PROFILE ENPOINTS
  public getSignatureProfile(): Observable<Response<SignatureProfile>> {
    const query = this.UrlAPI + QueryRequest.signProfile;
    return this.http.get<Response<SignatureProfile>>(query);
  }

  public setSignatureProfile(signProfile: SignatureProfile): Observable<Response<SignatureProfile>> {
    const query = this.UrlAPI + QueryRequest.signProfile;
    return this.http.post<Response<SignatureProfile>>(query, signProfile);
  }

  public getSignatureImage(): Observable<Response<SignatureImage>> {
    const query = this.UrlAPI + QueryRequest.signImage;
    return this.http.get<Response<SignatureImage>>(query);
  }

  public setSignatureImage(signImage: SignatureImage): Observable<Response<SignatureImage>> {
    const query = this.UrlAPI + QueryRequest.signImage;
    return this.http.put<Response<SignatureImage>>(query, signImage);
  }

  public getSignatureLastPosition(): Observable<Response<SignatureLastPosition>> {
    const query = this.UrlAPI + QueryRequest.signLastPosition;
    return this.http.get<Response<SignatureLastPosition>>(query);
  }

  public setSignatureLastPosition(signLastPosition: SignatureLastPosition): Observable<Response<SignatureLastPosition>> {
    const query = this.UrlAPI + QueryRequest.signLastPosition;
    return this.http.post<Response<SignatureLastPosition>>(query, signLastPosition);
  }
}
